import React, { useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import DragItemTypes from "../DragItemTypes";
import { getEmptyImage } from "react-dnd-html5-backend";
import {
  buildOrganizeViewPath,
  getCollections,
  OrganizeViews,
  groupChildrenByParent,
  setIds,
  UNGROUPED_ANTHOLOGY_CHILD_VIEW,
} from "../organizeUtil";
import { any, equals, includes, join, pluck, propEq } from "ramda";
import { useDeleteModuleItems } from "../../modules/moduleServices";
import {
  useAddToAnthology,
  useUpdateAnthologyItems,
} from "../../anthologies/anthologyServices";
import { useHistory } from "react-router-dom";
import { useParentDrop } from "../use-parentDrop";
import { useDrag } from "react-dnd";
import { notBlank } from "../../../util/ext";
import { useAnthologyCollectionsCreator } from "../../creatorServices";
import { useDnD, useSelected, useSidebar } from "../OrganizeProvider";

import TreeItem from "@material-ui/lab/TreeItem";

import CollapseIcon from "./CollapseIcon";
import SidebarCollectionTreeItem from "./SidebarCollectionTreeItem";
import SidebarTreeItemLabel from "./SidebarTreeItemLabel";

const useStyles = makeStyles(theme => ({
  anthology: {
    margin: 0,
    display: "flex",
    flexDirection: "column",
    fontWeight: "inherit",
  },

  anthologyIcon: {
    color: theme.palette.primary.main,
  },

  avatar: {
    height: 25,
    width: 25,
  },

  content: {
    backgroundColor: "transparent",
  },

  contentHighlighted: {
    backgroundColor: `${theme.palette.primary.main} !important`,
  },

  header: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: "1.6rem",
  },

  label: {
    color: "inherit",
  },

  labelHighlighted: {
    color: "white",
  },

  labelSubheading: {
    margin: 0,
    fontSize: "1.4rem",
    lineHeight: 1.4,
    fontWeight: theme.typography.fontWeightLight,
  },

  treeItem: {
    fontWeight: "inherit",
  },
}));

const buildUngroupedAnthologyChildNodeId = ({ parent, collection }) =>
  join("-", [
    OrganizeViews.ORGANIZE,
    OrganizeViews.UNGROUPED_ITEMS,
    OrganizeViews.ANTHOLOGY,
    OrganizeViews.COLLECTION,
    parent.id,
    collection.id,
  ]);

const SidebarUngroupedAnthologyTreeItem = props => {
  const classes = useStyles();
  const {
    child,
    count,
    handleMove,
    icon,
    label,
    nestedTree,
    nodeId,
    nodeType,
    onClick,
    parent,
    selected,
    showAlert,
    tooltipLabel,
    ...otherProps
  } = props;

  const anthology = child;
  const sidebar = useSidebar();
  const dnd = useDnD();
  const { selectedContentGroups } = useSelected();

  const history = useHistory();
  const [collections, setCollections] = useState([]);

  const anthologyCollections = useAnthologyCollectionsCreator(
    anthology.id,
    {
      limit: "all",
      sort: "collection.displayOrder asc",
    },
    {
      enabled: includes(nodeId, sidebar.expanded),
    },
  );
  useEffect(() => {
    if (anthologyCollections.isSuccess) {
      setCollections(anthologyCollections.data.contents);
    }
  }, [anthologyCollections.data, anthologyCollections.isSuccess]);

  const getTypographyProps = type => {
    switch (type) {
      case "root":
        return { classes: { root: classes.header } };
      case "anthology":
        return { classes: { root: classes.anthology } };
      default:
        return { classes: { root: classes.treeItem } };
    }
  };

  //-- Handle Drag and Drop Actions
  const addToAnthology = useAddToAnthology();
  const deleteModuleItems = useDeleteModuleItems();
  const updateAnthologyItems = useUpdateAnthologyItems();
  const onDrop = useCallback(
    async ({ items, dropAction }) => {
      try {
        if (equals(dropAction, "move")) {
          const prevParents = groupChildrenByParent(items);

          // Remove children from previous parents
          for (const { prevParent, newChildren } of prevParents) {
            if (notBlank(prevParent.id)) {
              const ids = setIds({
                mediaType: prevParent.mediaType,
                prevMediaList: prevParent.prevMediaList,
                itemsToRemove: newChildren.ids,
              });

              const body = { id: prevParent.id, ids };

              if (equals(prevParent.mediaType, "anthology")) {
                await updateAnthologyItems.mutateAsync({
                  resourceIds: { id: body.id },
                  data: { ids: ids },
                });
              }

              if (equals(prevParent.mediaType, "module")) {
                await deleteModuleItems.mutateAsync(body);
              }
            }
          }
        }

        await addToAnthology.mutateAsync({
          resourceIds: { id: anthology.id },
          data: { ids: pluck("id", items) },
        });

        const alert = equals(dropAction, "move")
          ? "Collection(s) Successfully Moved"
          : "Collection(s) Successfully Added";

        showAlert("success", [alert]);
      } catch (err) {
        showAlert("error", [err.message]);
      }
    },
    [
      addToAnthology,
      anthology,
      deleteModuleItems,
      showAlert,
      updateAnthologyItems,
    ],
  );

  const [{ isOver, canDrop }, drop] = useParentDrop({
    parent: anthology,
    onDrop,
    accept: [DragItemTypes.COLLECTION],
  });

  // Bug prevented drag when values passed to dep array
  // https://github.com/react-dnd/react-dnd/issues/3479
  const [, drag, preview] = useDrag(
    () => ({
      type: DragItemTypes.ANTHOLOGY,
      item: () => {
        dnd.updateDragAction("Move");
        return {
          id: anthology.id,
          mediaType: "anthology",
          prevMediaList: collections,
        };
      },
      end: () => dnd.updateDragAction(""),
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [collections],
  );

  // Create Draggable & Droppable Element
  // https://github.com/react-dnd/react-dnd/issues/1451
  const dragDropRef = useCallback(
    node => {
      drag(node);
      drop(node);
    },
    [drag, drop],
  );

  // This is required for creating a custom drag overlay
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  const handleLabelClick = ({ collection, anthology }) => event => {
    // if (event.ctrlKey || event.metaKey) {
    //   updateSelectedCollections({
    //     id: collection.id,
    //     parent: anthology,
    //     prevMediaList: collections,
    //   });
    //   return;
    // }
    // removeAllSelectedContent();
    const viewPath = buildOrganizeViewPath({
      view: UNGROUPED_ANTHOLOGY_CHILD_VIEW,
      ids: [anthology.id, collection.id],
    });
    history.push(viewPath);
  };

  return (
    <TreeItem
      ref={dragDropRef}
      nodeId={nodeId}
      onClick={onClick}
      classes={{
        content:
          (isOver && canDrop) || selected
            ? classes.contentHighlighted
            : classes.content,
        label:
          (isOver && canDrop) || selected
            ? classes.labelHighlighted
            : classes.label,
      }}
      collapseIcon={<CollapseIcon loading={anthologyCollections.isLoading} />}
      label={
        <SidebarTreeItemLabel
          item={{ child, parent: undefined, prevMediaList: undefined }}
          tooltipLabel={tooltipLabel}
          labelText={anthology.name}
          TypographyProps={getTypographyProps(nodeType)}
        />
      }
      {...otherProps}
    >
      {notBlank(collections) ? (
        collections.map(collection => (
          <SidebarCollectionTreeItem
            child={collection}
            key={buildUngroupedAnthologyChildNodeId({ parent, collection })}
            nodeId={buildUngroupedAnthologyChildNodeId({ parent, collection })}
            showAlert={showAlert}
            parentChildrenList={collections}
            onLabelClick={handleLabelClick({ collection, anthology })}
            selected={any(
              propEq("id", collection.id),
              getCollections(selectedContentGroups),
            )}
            tooltipLabel={collection.name}
            label={collection.name}
            parent={parent}
          />
        ))
      ) : (
        <div />
      )}
    </TreeItem>
  );
};

export default SidebarUngroupedAnthologyTreeItem;
